import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangPath } from '../../hooks/useLangPath';

const CASES = [
  {
    tag: 'Restaurant Tech · AI Optimization',
    color: '#45d0bd',
    icon: '🍽️',
    title: 'AI Cut Restaurant Operational Cost by 30%',
    result: '30%',
    resultLabel: 'Cost reduction',
    summary:
      'WIZ deployed DineOS across 8 restaurant branches — AI demand forecasting slashed food waste by 45% and self-ordering kiosks tripled order processing speed.',
    metrics: [
      { value: '45%', label: 'Less food waste' },
      { value: '3×',  label: 'Faster orders' },
      { value: '8',   label: 'Branches digitized' },
    ],
    href: '/case-studies',
  },
  {
    tag: 'HealthTech · Clinical Platform',
    color: '#8258c8',
    icon: '🏥',
    title: 'Zero-Error Hospital Platform for Clinical Precision',
    result: '0',
    resultLabel: 'Appointment errors',
    summary:
      "WIZ built a medical-grade platform that eliminated data-entry errors and digitized an entire hospital's workflow — from appointments to AI-assisted diagnostics.",
    metrics: [
      { value: '100%', label: 'Digital patient records' },
      { value: '60%',  label: 'Less admin overhead' },
      { value: '4.9★', label: 'Staff satisfaction' },
    ],
    href: '/case-studies',
  },
];

const CASES_AR = [
  {
    tag: 'تقنية المطاعم · تحسين بالذكاء الاصطناعي',
    color: '#45d0bd',
    icon: '🍽️',
    title: 'الذكاء الاصطناعي يخفض التكاليف التشغيلية للمطاعم بنسبة 30%',
    result: '30%',
    resultLabel: 'تخفيض التكاليف',
    summary: 'نشرت WIZ نظام DineOS عبر 8 فروع مطاعم — خفّض التوقع بالذكاء الاصطناعي هدر الطعام بنسبة 45% وضاعف أكشاك الطلب الذاتي سرعة معالجة الطلبات ثلاث مرات.',
    metrics: [
      { value: '45%', label: 'تقليل الهدر' },
      { value: '3×',  label: 'طلبات أسرع' },
      { value: '8',   label: 'فروع رُقمنت' },
    ],
    href: '/case-studies',
  },
  {
    tag: 'تقنية الصحة · منصة سريرية',
    color: '#8258c8',
    icon: '🏥',
    title: 'منصة مستشفى بلا أخطاء للدقة السريرية',
    result: '0',
    resultLabel: 'أخطاء مواعيد',
    summary: 'بنت WIZ منصة طبية بدرجة سريرية أزالت أخطاء إدخال البيانات ورقمنت سير عمل مستشفى بأكمله — من المواعيد إلى التشخيص بمساعدة الذكاء الاصطناعي.',
    metrics: [
      { value: '100%', label: 'سجلات رقمية' },
      { value: '60%',  label: 'تقليل الأعباء الإدارية' },
      { value: '4.9★', label: 'رضا الموظفين' },
    ],
    href: '/case-studies',
  },
];

const glass = {
  background: 'rgba(10,10,18,0.58)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
};

const Blog = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const getPath = useLangPath();
  const cases = isAr ? CASES_AR : CASES;

  return (
    <section className="blog-sec2 ibt-section-gap" style={{ background: 'transparent' }}>
      <div className="container">

        {/* Header */}
        <div className="row end" style={{ marginBottom: 44, alignItems: 'flex-end' }}>
          <div className="col-lg-8 col-md-7">
            <div className="sec-title mb-0">
              <h2 className="title animated-heading" style={{ color: '#fff' }}>
                {t('Real Impact,')}{' '}
                <span style={{
                  background: 'linear-gradient(102deg, #8258c8, #2c84c8)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>
                  {t('Proven Results')}
                </span>
              </h2>
              <p style={{ color: '#6b6b88', fontSize: '0.95rem', lineHeight: 1.7, marginTop: 10 }}>
                {t("Case studies that show what happens when WIZ-engineered software meets a real business challenge.")}
              </p>
            </div>
          </div>
          <div className="col-lg-4 col-md-5" style={{ textAlign: 'right' }}>
            <Link
              to={getPath('/case-studies')}
              className="ibt-btn ibt-btn-outline"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <span>{t('View All Case Studies')}</span>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
          </div>
        </div>

        {/* Case study cards — simple layout */}
        <div className="row g-4">
          {cases.map((c, i) => (
            <div className="col-lg-6" key={i}>
              <div style={{
                borderRadius: 16, padding: '28px 28px 24px',
                border: `1px solid ${c.color}28`,
                background: 'rgba(255,255,255,0.03)',
                height: '100%', display: 'flex', flexDirection: 'column', gap: 14,
              }}>
                {/* Icon + tag */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1.4rem' }}>{c.icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: c.color }}>{c.tag}</span>
                </div>

                {/* Stat + title */}
                <div>
                  <div style={{ fontSize: '2.4rem', fontWeight: 900, color: c.color, lineHeight: 1 }}>{c.result}</div>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', marginBottom: 8 }}>{c.resultLabel}</div>
                  <h4 style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, lineHeight: 1.4, margin: 0 }}>{c.title}</h4>
                </div>

                {/* Metrics row */}
                <div style={{ display: 'flex', gap: 10 }}>
                  {c.metrics.map((m, j) => (
                    <div key={j} style={{ flex: 1 }}>
                      <div style={{ color: c.color, fontWeight: 800, fontSize: '1rem' }}>{m.value}</div>
                      <div style={{ color: '#6b6b88', fontSize: '10px' }}>{m.label}</div>
                    </div>
                  ))}
                </div>

                {/* Read more */}
                <Link to={getPath(c.href)} style={{
                  marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                  color: c.color, fontSize: '13px', fontWeight: 700, textDecoration: 'none',
                }}>
                  {t('Read Full Study')}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Blog;
